import React, { useEffect, useState, useCallback } from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Paper, 
  Table, 
  TableBody, 
  TableRow, 
  TableCell, 
  TableContainer, 
  TableHead, 
  Typography, 
  IconButton,
  useTheme,
  alpha,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Skeleton,
  Chip,
  Stack
} from '@mui/material';
import { useParams } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Breadcrumb from 'components/Breadcrumb';
import { logger } from 'utils/logger';
import apiService from 'services/ApiService';
import { environment } from 'environments/environment';
import subscriptionsService, { UserSolutionSubscriptionItem } from './services/SubscriptionsService';
import { usersService } from './services/UsersService';
import AddSubscriptionDialog from './components/AddSubscriptionDialog';
import ConfirmationDialog from '../../../components/ConfirmationDialog';

// Create a component-specific logger
const log = logger.getLogger('UserSubscriptionsPage');

const UserSubscriptionsPage: React.FC = () => {
  const theme = useTheme();
  const { accountId, userId } = useParams<{ accountId: string; userId: string }>();
  
  const [subscriptions, setSubscriptions] = useState<UserSolutionSubscriptionItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [userDataLoaded, setUserDataLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [accountName, setAccountName] = useState<string>("");
  const [userName, setUserName] = useState<string>("");
  const [addDialogOpen, setAddDialogOpen] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<number | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [selectedSubscription, setSelectedSubscription] = useState<UserSolutionSubscriptionItem | null>(null);

  // Fetch account name
  const fetchAccountName = useCallback(async () => {
    if (!accountId) return;
    
    try {
      const response = await apiService.makeRequest(
        'get', 
        `${environment.services.account.baseUrl}${environment.services.account.basePath}/accounts/${accountId}`
      );
      setAccountName(response?.name || `Account #${accountId}`);
    } catch (err) {
      log.error('Error fetching account details:', err);
      setAccountName(`Account #${accountId}`);
    }
  }, [accountId]);

  // Fetch user details
  const fetchUserDetails = useCallback(async () => {
    if (!userId || !accountId) return;
    
    try {
      const user = await usersService.getUser(Number(userId));
      setUserName(`${user.firstName} ${user.lastName}`);
      setUserDataLoaded(true);
    } catch (err) {
      log.error('Error fetching user details:', err);
      setUserName(`User #${userId}`);
      setUserDataLoaded(true);
    }
  }, [userId, accountId]);

  // Fetch user subscriptions
  const fetchSubscriptions = useCallback(async () => {
    if (!userId || !accountId) return;
    
    try {
      setLoading(true);
      log.log(`Fetching subscriptions for user ${userId} in account ${accountId}...`);
      
      const result = await subscriptionsService.getUserSubscriptions(Number(userId), Number(accountId));
      
      // Add defensive check to ensure subscriptions is always an array
      if (result && result.data && Array.isArray(result.data)) {
        setSubscriptions(result.data);
      } else if (result && result.data && typeof result.data === 'object') {
        log.warn('Subscription data is not an array, attempting to convert:', result.data);
        // Handle case where data might be in a different format
        try {
          // If it's an object with numeric keys, try to convert to array
          const dataArray = Object.values(result.data);
          if (Array.isArray(dataArray)) {
            setSubscriptions(dataArray);
          } else {
            log.error('Unable to convert subscription data to array');
            setSubscriptions([]);
          }
        } catch (conversionError) {
          log.error('Error converting subscription data to array:', conversionError);
          setSubscriptions([]);
        }
      } else {
        log.error(`Invalid subscription data format received:`, result);
        setSubscriptions([]);
      }
      
      setError(null);
      
    } catch (err) {
      log.error('Error fetching user subscriptions:', err);
      setError('Failed to load subscriptions. Please try again later.');
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  }, [userId, accountId]);

  // Initial data fetch
  useEffect(() => {
    if (accountId && userId) {
      fetchAccountName();
      fetchUserDetails();
      fetchSubscriptions();
    }
  }, [accountId, userId, fetchAccountName, fetchUserDetails, fetchSubscriptions]);

  // Handle subscription add dialog
  const handleOpenAddDialog = useCallback(() => {
    setEditMode(false);
    setSelectedSubscription(null);
    setAddDialogOpen(true);
  }, []);

  const handleOpenEditDialog = useCallback((subscription: UserSolutionSubscriptionItem) => {
    setEditMode(true);
    setSelectedSubscription(subscription);
    setAddDialogOpen(true);
  }, []);

  const handleCloseAddDialog = useCallback(() => {
    setAddDialogOpen(false);
  }, []);

  // Handle subscription add/edit success
  const handleSubscriptionSaved = useCallback((e?: React.SyntheticEvent) => {
    // Prevent default form submission behavior if event is provided
    if (e) {
      e.preventDefault();
    }
    
    // Refresh the list of subscriptions
    fetchSubscriptions();
    
    // Show success message
    setSuccessMessage(editMode ? "Subscription updated successfully" : "Subscription added successfully");
    
    // Close the dialog
    setAddDialogOpen(false);
    
    // Explicitly stay on the current page by doing nothing with navigation
    // This prevents any unintended redirects
  }, [fetchSubscriptions, editMode]);

  // Handle delete confirmation dialog
  const handleOpenDeleteConfirmation = useCallback((subscriptionId: number) => {
    setSelectedSubscriptionId(subscriptionId);
    setConfirmDeleteOpen(true);
  }, []);

  const handleCloseDeleteConfirmation = useCallback(() => {
    setConfirmDeleteOpen(false);
    setSelectedSubscriptionId(null);
  }, []);

  // Handle subscription removal
  const handleRemoveSubscription = useCallback(async () => {
    if (!userId || !selectedSubscriptionId) return;
    
    try {
      await subscriptionsService.removeSubscriptionFromUser(Number(userId), selectedSubscriptionId);
      
      // Refresh the subscriptions list
      fetchSubscriptions();
      
      // Show success message
      setSuccessMessage("Subscription removed successfully");
      
      // Close confirmation dialog
      handleCloseDeleteConfirmation();
    } catch (err) {
      log.error('Error removing subscription:', err);
      setErrorMessage("Failed to remove subscription. Please try again.");
      handleCloseDeleteConfirmation();
    }
  }, [userId, selectedSubscriptionId, fetchSubscriptions, handleCloseDeleteConfirmation]);

  // Handle snackbar close
  const handleCloseSuccessMessage = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSuccessMessage(null);
  };

  const handleCloseErrorMessage = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setErrorMessage(null);
  };

  return (
    <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
      <Breadcrumb 
        items={[
          {
            label: 'Accounts',
            path: '/accounts',
            icon: <BusinessIcon fontSize="small" sx={{ fontSize: '1rem' }} />
          },
          { 
            label: accountName,
            path: `/accounts/${accountId}/edit`,
            icon: <BusinessIcon fontSize="small" sx={{ fontSize: '1rem' }} />
          },
          {
            label: 'Users',
            path: `/accounts/${accountId}/users`,
            icon: <PersonIcon fontSize="small" sx={{ fontSize: '1rem' }} />
          },
          {
            label: `Subscriptions - ${userName}`,
            icon: <PersonIcon fontSize="small" sx={{ fontSize: '1rem' }} />
          }
        ]}
      />
      
      
      
      {/* Loading skeleton displayed until user data is loaded */}
      {!userDataLoaded && (
        <Card
          elevation={2}
          sx={{
            width: '100%',
            mb: 3,
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <Skeleton variant="circular" width={56} height={56} sx={{ mr: 2 }} />
              <Skeleton variant="text" width={200} height={40} />
            </Box>
            <Skeleton variant="text" width="90%" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="70%" height={20} />
          </CardContent>
        </Card>
      )}
      
      <Paper 
        elevation={2}
        sx={{ 
          width: '100%', 
          mb: 3, 
          borderRadius: 2,
          overflow: 'hidden',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0 8px 16px 0 rgba(0,0,0,0.1)'
          }
        }}
      >
        <Box 
          p={2.5} 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          sx={{
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            background: theme.palette.background.default
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Subscription Access
          </Typography>
          
          <Button 
            variant="contained" 
            color="primary" 
            size="medium"
            onClick={handleOpenAddDialog}
            startIcon={<AddIcon />}
            sx={{ 
              px: 2,
              py: 1,
              borderRadius: '12px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 14px rgba(0,0,0,0.14)'
              }
            }}
          >
            Add Subscription
          </Button>
        </Box>

        <TableContainer>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.875rem',
                    color: theme.palette.text.primary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.025em',
                    py: 1.5,
                    borderBottom: `2px solid ${alpha(theme.palette.divider, 0.7)}`,
                    backgroundColor: theme.palette.background.default,
                    width: '30%'
                  }}
                >
                  Solution
                </TableCell>
                <TableCell 
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.875rem',
                    color: theme.palette.text.primary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.025em',
                    py: 1.5,
                    borderBottom: `2px solid ${alpha(theme.palette.divider, 0.7)}`,
                    backgroundColor: theme.palette.background.default,
                    width: '30%'
                  }}
                >
                  Subscription
                </TableCell>
                <TableCell 
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.875rem',
                    color: theme.palette.text.primary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.025em',
                    py: 1.5,
                    borderBottom: `2px solid ${alpha(theme.palette.divider, 0.7)}`,
                    backgroundColor: theme.palette.background.default
                  }}
                >
                  Schedules
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.875rem',
                    color: theme.palette.text.primary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.025em',
                    py: 1.5,
                    borderBottom: `2px solid ${alpha(theme.palette.divider, 0.7)}`,
                    backgroundColor: theme.palette.background.default,
                    width: '15%'
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              sx={{
                '& .MuiTableRow-root:nth-of-type(odd)': {
                  backgroundColor: alpha(theme.palette.background.default, 0.4),
                }
              }}
            >
              {loading && (
                <>
                  {[1, 2, 3].map((item) => (
                    <TableRow key={item}>
                      <TableCell>
                        <Skeleton variant="text" width="70%" height={24} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width="70%" height={24} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width="100%" height={24} />
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center">
                          <Skeleton variant="circular" width={32} height={32} sx={{ mx: 0.5 }} />
                          <Skeleton variant="circular" width={32} height={32} sx={{ mx: 0.5 }} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              )}
              
              {!loading && error && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ color: 'error.main' }}>
                    {error}
                  </TableCell>
                </TableRow>
              )}
              
              {!loading && !error && subscriptions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Box sx={{ py: 3, textAlign: 'center' }}>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        No subscriptions found for this user.
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAddDialog}
                        sx={{
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: 600
                        }}
                      >
                        Add Subscription
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              
              {!loading && !error && subscriptions?.map((item) => (
                <TableRow 
                  key={item.id}
                  sx={{ 
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: `${alpha(theme.palette.primary.light, 0.08)} !important`,
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  <TableCell>
                    <Typography variant="body1" fontWeight={500}>
                      {item.solution?.name || 'Unknown Solution'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1">
                      {item.subscription?.name || 'Unknown Subscription'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {item.summarySchedules && item.summarySchedules.length > 0 ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {item.summarySchedules.map((schedule) => (
                          <Chip
                            key={schedule.id}
                            icon={<CalendarMonthIcon />}
                            label={schedule.name}
                            size="small"
                            sx={{ 
                              my: 0.5,
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              fontWeight: 500,
                              '& .MuiChip-icon': {
                                color: theme.palette.primary.main
                              }
                            }}
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        No schedules
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center">
                      <IconButton 
                        onClick={() => handleOpenEditDialog(item)} 
                        color="primary"
                        sx={{ 
                          mx: 0.5,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleOpenDeleteConfirmation(item.id)} 
                        color="error"
                        sx={{ 
                          mx: 0.5,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      <AddSubscriptionDialog 
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        accountId={Number(accountId)}
        userId={Number(userId)}
        userName={userName}
        onSubscriptionSaved={handleSubscriptionSaved}
        editMode={editMode}
        subscription={selectedSubscription}
      />
      
      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDeleteOpen}
        title="Remove Subscription"
        content="Are you sure you want to remove this subscription? This action cannot be undone."
        onCancel={handleCloseDeleteConfirmation}
        onConfirm={handleRemoveSubscription}
      />
      
      {/* Success Message */}
      <Snackbar 
        open={!!successMessage} 
        autoHideDuration={6000} 
        onClose={handleCloseSuccessMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSuccessMessage} 
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
      
      {/* Error Message */}
      <Snackbar 
        open={!!errorMessage} 
        autoHideDuration={6000} 
        onClose={handleCloseErrorMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseErrorMessage} 
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserSubscriptionsPage;